<template>
  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">System Monitoring</p>
        <h1 class="page-title"><RectangleEllipsis :size="26" /> Health pings</h1>
        <p class="page-subtitle">Monitor recent application pings by organization, facility, user, and timestamp.</p>
      </div>
      <button class="ui-button primary" @click="refreshData" :disabled="loading">
        <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Total pings</span>
        <strong class="stat-value">{{ healthPings.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ filteredData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Known users</span>
        <strong class="stat-value">{{ users.length }}</strong>
      </div>
    </div>

    <div v-if="error" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ error }}
    </div>

    <div class="ui-card module-filter-card">
      <div class="form-grid">
        <div class="form-section full-width">
          <label class="field-label">Search</label>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search organization, facility, user..."
            class="ui-input"
            @input="currentPage = 1"
          >
        </div>
        <div class="form-section">
          <label class="field-label">Rows per page</label>
          <select v-model="limit" class="ui-select" @change="currentPage = 1">
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Facility UUID</th>
            <th>User</th>
            <th>Timestamp</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="4">
              <div class="empty-state">Loading health pings...</div>
            </td>
          </tr>
          <tr v-else v-for="(ping, index) in paginatedData" :key="`${ping.facilityUuid}-${ping.userID}-${index}`">
            <td data-label="Organization">
              <span class="table-primary">{{ ping.organizationName || 'Unknown organization' }}</span>
            </td>
            <td data-label="Facility UUID">
              <code class="code-pill">{{ ping.facilityUuid || '—' }}</code>
            </td>
            <td data-label="User">
              <span class="table-primary">{{ getUserName(ping.userID) || ping.userID || 'Unknown user' }}</span>
            </td>
            <td data-label="Timestamp">{{ formatDateTime(ping.timestamp) }}</td>
          </tr>
          <tr v-if="!loading && paginatedData.length === 0">
            <td colspan="4">
              <div class="empty-state">No health pings found for the current filters.</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="filteredData.length">
        <span class="table-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getHealthPings } from '@/services/dashboardServices';
import { getAllUser } from "@/services/userServices";
import { useAuthStore } from '@/stores/authStore';
import { RectangleEllipsis, ChevronLeft, ChevronRight } from 'lucide-vue-next';

export default {
  name: 'HealthPings',
  components: {
    RectangleEllipsis,
    ChevronLeft,
    ChevronRight,
  },
  data() {
    return {
      healthPings: [],
      users: [],
      userMap: {},
      loading: true,
      error: null,
      authStore: useAuthStore(),
      searchQuery: '',
      currentPage: 1,
      limit: 10
    };
  },
  computed: {
    filteredData() {
      if (!this.searchQuery) return this.healthPings;

      const query = this.searchQuery.toLowerCase();
      return this.healthPings.filter(item => {
        const userName = this.getUserName(item.userID) || '';

        return String(item.organizationName || '').toLowerCase().includes(query) ||
          String(item.facilityUuid || '').toLowerCase().includes(query) ||
          String(item.userID || '').toLowerCase().includes(query) ||
          userName.toLowerCase().includes(query);
      });
    },
    paginatedData() {
      const startIndex = (this.currentPage - 1) * Number(this.limit);
      return this.filteredData.slice(startIndex, startIndex + Number(this.limit));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / Number(this.limit)));
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        await this.fetchUsers();
        const data = await getHealthPings();
        this.healthPings = data || [];
        this.currentPage = 1;
      } catch (err) {
        console.error('Error fetching health pings:', err);
        this.error = 'Failed to load health pings data. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers() {
      try {
        const result = await getAllUser();
        if (result && result.users) {
          this.users = result.users;
          this.userMap = {};
          for (const user of this.users) {
            this.userMap[user.uuid] = user;
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    getUserName(userID) {
      const user = this.userMap[userID];
      return user ? user.full_name : null;
    },
    refreshData() {
      this.fetchData();
    },
    changePage(page) {
      this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '—';
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('en-MY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kuala_Lumpur'
      }).format(date);
    }
  }
};
</script>

<style scoped>
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-filter-card {
  padding: 20px;
  margin-bottom: 18px;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.code-pill {
  display: inline-flex;
  max-width: 260px;
  padding: 5px 8px;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
